import type { LoginMutationVariables, RegisterCustomerInput, RequestPasswordResetMutationVariables, ResetPasswordMutationVariables, VerifyCustomerAccountMutationVariables } from '#graphql-operations'
import { DeletionResult, ErrorCode } from '#graphql-operations'
import { InvalidCredentialsError, MissingPasswordError, NativeAuthStrategyError, NotVerifiedError, PasswordAlreadySetError, PasswordResetTokenExpiredError, PasswordResetTokenInvalidError, PasswordValidationError, VerificationTokenExpiredError, VerificationTokenInvalidError } from './errors'

export async function activeCustomer() {
  const { data, errors } = await useGraphqlQuery('activeCustomer')

  if (errors?.length)
    throw new Error(errors[0]?.message || 'Failed to load the signed-in customer')

  return data.activeCustomer
}

export async function login(body: LoginMutationVariables) {
  const { data, errors } = await useGraphqlMutation('login', body)

  if (errors?.length)
    console.error('GraphQL errors:', errors)

  if (isGraphQLError(data.login)) {
    switch (data.login.errorCode) {
      case ErrorCode.INVALID_CREDENTIALS_ERROR: {
        if ('authenticationError' in data.login && data.login.authenticationError) {
          throw new InvalidCredentialsError(data.login.message, data.login.authenticationError)
        }
        throw new InvalidCredentialsError(data.login.message)
      }
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.login.message)
      }
      case ErrorCode.NOT_VERIFIED_ERROR: {
        throw new NotVerifiedError(data.login.message)
      }
      default: {
        throw new Error(data.login.message)
      }
    }
  }

  return data.login
}

export async function logout() {
  const { data, errors } = await useGraphqlMutation('logout')

  if (errors?.length)
    throw new Error(errors[0]?.message || 'Failed to sign out')

  return data.logout
}

/**
 * Soft-deletes the logged-in customer and their user (see the backend
 * `CustomerDeletePlugin`). The session survives the deletion, so callers still
 * have to log out afterwards.
 */
export async function deleteMe() {
  const { data, errors } = await useGraphqlMutation('deleteMe')

  if (errors?.length)
    console.error('GraphQL errors:', errors)

  if (data.deleteMe.result !== DeletionResult.DELETED)
    throw new Error(data.deleteMe.message ?? 'Account was not deleted')

  return data.deleteMe
}

export async function registerCustomerAccount(input: WithRequired<RegisterCustomerInput, 'password'>) {
  const { data, errors } = await useGraphqlMutation('registerCustomerAccount', { input })

  if (errors?.length)
    console.error('GraphQL errors:', errors)

  if (isGraphQLError(data.registerCustomerAccount)) {
    switch (data.registerCustomerAccount.errorCode) {
      case ErrorCode.MISSING_PASSWORD_ERROR: {
        throw new MissingPasswordError(data.registerCustomerAccount.message)
      }
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.registerCustomerAccount.message)
      }
      case ErrorCode.PASSWORD_VALIDATION_ERROR: {
        if ('validationErrorMessage' in data.registerCustomerAccount && data.registerCustomerAccount.validationErrorMessage)
          throw new PasswordValidationError(data.registerCustomerAccount.message, data.registerCustomerAccount.validationErrorMessage)
        throw new PasswordValidationError(data.registerCustomerAccount.message)
      }
      default: {
        throw new Error(data.registerCustomerAccount.message)
      }
    }
  }

  return data.registerCustomerAccount
}

export async function verifyCustomerAccount(body: VerifyCustomerAccountMutationVariables) {
  const { data, errors } = await useGraphqlMutation('verifyCustomerAccount', body)

  if (errors?.length)
    console.error('GraphQL errors:', errors)

  if (isGraphQLError(data.verifyCustomerAccount)) {
    switch (data.verifyCustomerAccount.errorCode) {
      case ErrorCode.MISSING_PASSWORD_ERROR: {
        throw new MissingPasswordError(data.verifyCustomerAccount.message)
      }
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.verifyCustomerAccount.message)
      }
      case ErrorCode.PASSWORD_ALREADY_SET_ERROR: {
        throw new PasswordAlreadySetError(data.verifyCustomerAccount.message)
      }
      case ErrorCode.PASSWORD_VALIDATION_ERROR: {
        if ('validationErrorMessage' in data.verifyCustomerAccount && data.verifyCustomerAccount.validationErrorMessage)
          throw new PasswordValidationError(data.verifyCustomerAccount.message, data.verifyCustomerAccount.validationErrorMessage)
        throw new PasswordValidationError(data.verifyCustomerAccount.message)
      }
      case ErrorCode.VERIFICATION_TOKEN_EXPIRED_ERROR: {
        throw new VerificationTokenExpiredError(data.verifyCustomerAccount.message)
      }
      case ErrorCode.VERIFICATION_TOKEN_INVALID_ERROR: {
        throw new VerificationTokenInvalidError(data.verifyCustomerAccount.message)
      }
      default: {
        throw new Error(data.verifyCustomerAccount.message)
      }
    }
  }

  return data.verifyCustomerAccount
}

export async function resetPassword(body: ResetPasswordMutationVariables) {
  const { data, errors } = await useGraphqlMutation('resetPassword', body)

  if (errors?.length)
    console.error('GraphQL errors:', errors)

  if (isGraphQLError(data.resetPassword)) {
    switch (data.resetPassword.errorCode) {
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.resetPassword.message)
      }
      case ErrorCode.NOT_VERIFIED_ERROR: {
        throw new NotVerifiedError(data.resetPassword.message)
      }
      case ErrorCode.PASSWORD_RESET_TOKEN_EXPIRED_ERROR: {
        throw new PasswordResetTokenExpiredError(data.resetPassword.message)
      }
      case ErrorCode.PASSWORD_RESET_TOKEN_INVALID_ERROR: {
        throw new PasswordResetTokenInvalidError(data.resetPassword.message)
      }
      case ErrorCode.PASSWORD_VALIDATION_ERROR: {
        if ('validationErrorMessage' in data.resetPassword && data.resetPassword.validationErrorMessage)
          throw new PasswordValidationError(data.resetPassword.message, data.resetPassword.validationErrorMessage)
        throw new PasswordValidationError(data.resetPassword.message)
      }
    }
  }

  return data.resetPassword
}

export async function requestPasswordReset(body: RequestPasswordResetMutationVariables) {
  const { data, errors } = await useGraphqlMutation('requestPasswordReset', body)

  if (errors?.length)
    console.error('GraphQL errors:', errors)

  if (isGraphQLError(data.requestPasswordReset)) {
    switch (data.requestPasswordReset.errorCode) {
      case ErrorCode.NATIVE_AUTH_STRATEGY_ERROR: {
        throw new NativeAuthStrategyError(data.requestPasswordReset.message)
      }
      default: {
        throw new Error(data.requestPasswordReset.message)
      }
    }
  }

  return data.requestPasswordReset
}
